/**
 * Pure derivations for the completion rail, split out of the component file
 * so react-refresh sees a components-only module (the two exports here are
 * plain functions — routed by the motion session 2026-08-12).
 */

import { formatClockTime, getTimeFormat } from '@/shared/lib/time-format';
import type { CompletionResolution } from './composition-engine';

/**
 * Live resolution from the form the manual sections write — the rail's
 * second input besides the draft. Pure + exported so tests assert the
 * mapping without mounting the panel.
 *
 * `patient` collapses to resolved/unsearched here: the ambiguous/none
 * split needs the patient search's match count, which is
 * PatientSection-local state (r2 seam — the section will report it up
 * once the in-card picker lands).
 */
export function deriveCompletionResolution(v: {
  patientId: string | null;
  providerId: string | null;
  appointmentProfileId: string | null;
  slotStart: string | null;
  slotEnd: string | null;
  recurringSlotCount: number;
  hasRecurrence: boolean;
  /** A downstream rejection to surface (PROD-1846 verdict, race loss). */
  conflict?: string;
}): CompletionResolution {
  return {
    patient: v.patientId ? 'resolved' : 'unsearched',
    practitionerResolved: !!v.providerId,
    visitTypeResolved: !!v.appointmentProfileId,
    slotResolved: !!v.slotStart && !!v.slotEnd,
    ...(v.hasRecurrence ? { recurrenceResolved: v.recurringSlotCount > 0 } : {}),
    ...(v.conflict ? { conflict: v.conflict } : {}),
  };
}

/** "Tue, 11 Aug · 10:00" (or "10:00am" under the 12-hour preference) from the draft's local no-Z ISO start. */
export function formatDraftWhen(start: string | undefined): string {
  if (!start) return '';
  const [dpart, tpart = '00:00'] = start.split('T');
  const [y, m, dd] = dpart.split('-').map(Number);
  const [hh, mm] = tpart.split(':').map(Number);
  const dt = new Date(y, (m ?? 1) - 1, dd ?? 1, hh ?? 0, mm ?? 0);
  // 24-hour "10:00"; the 12-hour preference keeps this line's "10:00am".
  const time = getTimeFormat() === '12h' ? `${formatClockTime(dt, '12h')}m` : formatClockTime(dt, '24h');
  return `${dt.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} · ${time}`;
}

